import {
  useTranslate, TextField, EditButton, DeleteButton, CreateButton, TopToolbar,
  SimpleForm, required, Toolbar,
  SaveButton, BooleanInput,
  useRecordContext,
  Button,
  Confirm,
  useSaveContext,
  useDataProvider,
  useRefresh,
  useNotify,
  useResourceContext,
  ReferenceArrayInput,
} from 'react-admin';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import {
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ListLive } from '@react-admin/ra-realtime';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { CertificationCategory, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import SearchFields from '../components/fields/Filters';
import { LIST_DEBOUNCE } from '../../../config';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { CreateToolbarRight } from '../../../components/CustomToolBar';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayExpiryTimeGranularityInputWithUpperLabel';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { CertifiedUsersButton } from '../../../components/buttons/CertifiedUsersButton';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { GrayAutocompleteArrayInputWithUpperLabel } from '../components/inputs/GrayAutocompleteArrayInputWithUpperLabel';

type CertificationCategoryPresetKey =
  | 'basic_emergency_response_officer'
  | 'bhv_team_leader'
  | 'head_of_emergency_response'
  | 'basic_vca'
  | 'vol_vca'
  | 'forklift_certificate'
  | 'reach_truck_certificate'
  | 'aerial_work_platform_certificate'
  | 'nen_3140_skilled_person'
  | 'nen_3140_installation_responsible'
  | 'respiratory_protection'
  | 'working_at_height'
  | 'basic_first_aid'
  | 'pediatric_first_aid'
  | 'sports_first_aid'
  | 'first_aid_refresher'
  | 'cpr_aed';

type CertificationCategoryPreset = {
  presetKey: CertificationCategoryPresetKey;
  validityPeriod: CertificationCategory['validityPeriod'];
};

const PRESET_I18N_PREFIX = 'resources.certification-categories.presets';

const certificationPresets: CertificationCategoryPreset[] = [
  {
    presetKey: 'basic_emergency_response_officer',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'bhv_team_leader',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'head_of_emergency_response',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'basic_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'vol_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'forklift_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'reach_truck_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'aerial_work_platform_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'nen_3140_skilled_person',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'nen_3140_installation_responsible',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'respiratory_protection',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'working_at_height',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'basic_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'pediatric_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'sports_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'first_aid_refresher',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'cpr_aed',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
];

const presetToCreatePayload = (
  preset: CertificationCategoryPreset,
  translate: (key: string) => string,
): Omit<CertificationCategory, '_id' | 'customer' | 'share' | 'createdAt' | 'updatedAt'> => ({

  name: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.name`),
  description: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.description`),
  validityPeriod: preset.validityPeriod,
  notifiedPeople: { peopleToNotify: [], isCertificationHolderNotified: true },
  isDocumentaryProofRequired: false,

});

const CertificationCreateFromPresetsDialog = (props: { open: boolean; onClose: () => void }) => {

  const [selectedItems, setSelectedItems] = useState<CertificationCategoryPresetKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleClose = () => {

    setSelectedItems([]);
    props.onClose();

  };

  const handleToggleSelect = (presetKey: CertificationCategoryPresetKey) => {

    setSelectedItems((current) => {

      if (current.includes(presetKey)) return current.filter((item) => item !== presetKey);
      return [...current, presetKey];

    });

  };

  const handleAdd = async () => {

    setIsLoading(true);

    try {

      const selected = certificationPresets.filter((row) => selectedItems.includes(row.presetKey));

      if (selected.length === 0) {

        return;

      }

      await Promise.all(
        selected.map((item) => dataProvider.create(
          resource || 'certification-categories',
          { data: presetToCreatePayload(item, translate) },
        )),
      );

      notify(`${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`, { type: 'success' });
      refresh();

      handleClose();

    } catch (error) {

      if (error instanceof Error) {

        notify(error.message, { type: 'error' });

      }

    } finally {

      setIsLoading(false);

    }

  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{translate('resources.certification-categories.text.add_from_presets_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{translate('resources.certification-categories.text.add_from_presets_subtitle')}</DialogContentText>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{translate('general.fields.name')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{translate('resources.certification-categories.fields.validity_period')}</TableCell>
                <TableCell>{translate('general.fields.description')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certificationPresets.map((row) => {

                const isSelected = selectedItems.includes(row.presetKey);

                return (
                  <TableRow key={row.presetKey} onClick={() => handleToggleSelect(row.presetKey)} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        value={row.presetKey}
                      />
                    </TableCell>
                    <TableCell>{translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.name`)}</TableCell>
                    <TableCell>
                      {row.validityPeriod.value} {translate(`resources.report-triggers.text.granularity_types.${row.validityPeriod.type}`).toLowerCase()}
                    </TableCell>
                    <TableCell>{translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.description`)}</TableCell>
                  </TableRow>
                );

              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button label="general.text.close" size="medium" onClick={handleClose} />
        <Button label="general.text.add" size="medium" onClick={handleAdd}
          startIcon={isLoading ? <CircularProgress color='inherit' size={20} /> : undefined} disabled={selectedItems.length === 0 || isLoading} />
      </DialogActions>
    </Dialog>
  );

};

const CertificationDeleteButton = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  return (
    <DeleteButton
      mutationMode="pessimistic"
      confirmTitle={`${translate('ra.action.delete')} ${translate('resources.certification-categories.text.title_single').toLowerCase()} "${record?.name}"`}
    />
  );

};

export const CertificationList = () => {

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const translate = useTranslate();

  return (
    <>
      <h3>{translate('resources.certification-categories.title')}</h3>
      <ListLive
        filters={SearchFields} debounce={LIST_DEBOUNCE} title="resources.certification-categories.title" actions={
          <TopToolbar>
            <Button label="general.text.add_from_presets" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateDialogOpen(true)} />
            <CreateButton label="general.text.add" variant="contained" size="medium" startIcon={<AddIcon/>} />
          </TopToolbar>
        }>
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <TextField source="name" label="general.fields.name"/>
          <CertifiedUsersButton label="resources.certification-categories.text.count"/>
          <TimeGranularityField source="validityPeriod" label="resources.certification-categories.fields.validity_period"/>
          <AssetsInDialogButton
            label="resources.certification-categories.fields.people_to_notify"
            resource="certification-categories" source="notifiedPeople.peopleToNotify"/>
          <TextField source="description" label="general.fields.description"/>
          <IfCanAccess action="edit">
            <EditButton/>
          </IfCanAccess>
          <IfCanAccess action="delete">
            <CertificationDeleteButton/>
          </IfCanAccess>
        </Datagrid>
      </ListLive>
      <CertificationCreateFromPresetsDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}/>
    </>
  );

};

const certificationTransformer = (data: any) => {

  return {
    ...data,
    notifiedPeople: {
      peopleToNotify: (data.notifiedPeople.peopleToNotify ?? []).map((personId: string) => ({
        _id: personId,
        _ref: 'Asset',
      })),
      isCertificationHolderNotified: data.notifiedPeople.isCertificationHolderNotified,
    },
  };

};

const CertificationFields = () => {

  return (
    <>
      <GrayTextInputWithUpperLabel source="name" validate={required()} label="general.fields.name"/>
      <GrayExpiryTimeGranularityInputWithUpperLabel label={'resources.certification-categories.fields.validity_period'} source="validityPeriod"/>
      <BooleanInput source="notifiedPeople.isCertificationHolderNotified" label="resources.certification-categories.fields.is_certification_holder_notified"/>
      <ReferenceArrayInput source="notifiedPeople.peopleToNotify" reference="app-users" label="resources.certification-categories.fields.people_to_notify">
        <GrayAutocompleteArrayInputWithUpperLabel label="resources.certification-categories.fields.people_to_notify"/>
      </ReferenceArrayInput>
      <BooleanInput source="isDocumentaryProofRequired" label="resources.certification-categories.fields.is_documentary_proof_required"/>
      <GrayTextInputWithUpperLabel source="description" label="general.fields.description" multiline rows={4}/>
    </>
  );

};

const CertificationEditHeading = () => {

  const record = useRecordContext();
  const translate = useTranslate();

  return (
    <h4 style={{ marginBottom: '10px' }}>{translate('general.text.edit')} {'"'}{record?.name}{'"'}</h4>
  );

};

export const CertificationCreate = () => {

  const translate = useTranslate();
  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`;

  return (
    <CreateWithSuccess
      title="resources.certification-categories.title"
      toastLabel={toastLabel}
      transform={certificationTransformer}
    >
      <SimpleForm toolbar={<CreateToolbarRight narrow/>} sx={{ maxWidth: '1000px' }}>
        <h4>{translate('resources.certification-categories.text.create_title')}</h4>
        <Typography variant="body1">{translate('resources.certification-categories.text.category_explanation')}</Typography>
        <hr/>
        <CertificationFields/>
      </SimpleForm>
    </CreateWithSuccess>
  );

};

const SaveWithDocumentaryProofConfirm = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  const { handleSubmit } = useFormContext();
  const { save } = useSaveContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const wasInitiallyRequired = useRef<boolean | undefined>();

  useEffect(() => {

    if (!record) {

      return;

    }
    wasInitiallyRequired.current = record.isDocumentaryProofRequired;

  }, [record]);

  const runSave = (values: Partial<CertificationCategory>) => {

    save?.(values);

  };
  const trySave = handleSubmit((values) => {

    if (
      wasInitiallyRequired.current
      && values.isDocumentaryProofRequired === false
    ) {

      setConfirmOpen(true);
      return;

    }
    runSave(values);

  });
  return (
    <>
      <SaveButton label="ra.action.save" variant="contained" onClick={trySave} />
      <Confirm
        isOpen={confirmOpen}
        title={translate('resources.certification-categories.text.edit_confirmation_dialog_title')}
        content={translate('resources.certification-categories.text.edit_confirmation_dialog_content')}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {

          setConfirmOpen(false);
          handleSubmit(runSave)();

        }}
      />
    </>
  );

};

export const CertificationEdit = () => {

  const navigate = useNavigate();
  const translate = useTranslate();
  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.updated').toLowerCase()}`;

  return (
    <EditWithSuccess
      title="resources.certification-categories.title"
      actions={false}
      toastLabel={toastLabel}
      transform={certificationTransformer}
    >
      <SimpleForm toolbar={
        <Toolbar sx={{ justifyContent: 'end' }}>
          <Button label="ra.action.back" onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
          <SaveWithDocumentaryProofConfirm/>
        </Toolbar>}
      sx={{ maxWidth: '1000px' }}>
        <CertificationEditHeading/>
        <CertificationFields/>
      </SimpleForm>
    </EditWithSuccess>
  );

};
